import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonDirective } from '@ps-gui/shared-core';
import { AbstractBasicSignalInput } from '../abstract-basic-signal-input.component';
import { form, FormField } from '@angular/forms/signals';
import { DropdownOption } from '../../inputs/dropdown/dropdown.component';
import { TextFieldComponent } from '../text-field/text-field.component';

@Component({
  selector: 'shared-ui-dropdown-signal',
  standalone: true,
  imports: [
    NgClass,
    ButtonDirective,
    ReactiveFormsModule,
    NgTemplateOutlet,
    FormField,
    TextFieldComponent,
  ],
  templateUrl: './dropdown-signal.component.html',
})
export class DropdownSignalComponent<T> extends AbstractBasicSignalInput<T> {
  readonly dropdownOptions = input.required<DropdownOption<T>[]>();
  readonly enableSearch = input(false);
  readonly enableAddButton = input(false);
  readonly addInputPlaceholder = input('Neuer Wert...');

  override readonly placeholder = input('Auswählen...');

  valueAdded = output<DropdownOption<T>>();
  valueSelected = output();

  readonly menuButton = viewChild<ElementRef<HTMLButtonElement>>('menuButton');

  readonly searchString = signal('');
  searchStringForm = form(this.searchString);
  readonly customValue = signal('');
  customValueForm = form(this.customValue);

  readonly isOpened = linkedSignal(() => this.isFocused());

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.code !== 'Escape') {
      return;
    }

    this.isOpened.set(false);
  };

  private readonly onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!this.isOpened() || this.menuButton()?.nativeElement.contains(target)) return;
    this.isOpened.set(false);
  };

  private readonly addClickListener = () => {
    window.addEventListener('click', this.onClick);
  };
  private readonly addKeydownListener = () => {
    window.addEventListener('keydown', this.onKeyDown);
  };

  private readonly removeClickListener = () => {
    window.removeEventListener('click', this.onClick);
  };

  private readonly removeKeydownListener = () => {
    window.removeEventListener('keydown', this.onKeyDown);
  };

  constructor() {
    super();

    effect(() => {
      if (this.isOpened()) {
        this.addClickListener();
        this.addKeydownListener();
        return;
      }

      this.removeClickListener();
      this.removeKeydownListener();
    });
  }

  toggleMenu() {
    if (this.disabled()) return;
    if (this.readonly()) return;
    this.isOpened.update((bool) => !bool);
    this.searchStringForm().reset('');
    this.customValueForm().reset('');

    if (!this.isOpened()) return;
    this.clicked.emit();
  }

  readonly filteredOptions = computed(() =>
    this.dropdownOptions().filter((x) =>
      x.label.toLowerCase().startsWith(this.searchStringForm().value().toLowerCase()),
    ),
  );

  readonly amountHiddenOptions = computed(
    () => this.dropdownOptions().length - this.filteredOptions().length,
  );

  addOption() {
    const value = this.customValueForm().value();
    this.value.set(value as T);
    this.valueAdded.emit({ label: value, value: value as T });
  }

  onValueSelected(option: DropdownOption<T>): void {
    this.value.set(option.value);
    this.valueSelected.emit();
    this.isOpened.set(false);
  }

  readonly selectedOption = computed(
    () => this.dropdownOptions().find((option) => option.value == this.value()) ?? undefined,
  );
}
